/**
 * Admin Dashboard Page
 * Overview with statistics and charts
 */
// @ts-nocheck - Temporary fix for react-icons type compatibility with Chakra UI
import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  Spinner,
  Flex,
  useToast,
  Icon,
  Heading,
} from '@chakra-ui/react';
import { FiUsers, FiTrendingUp, FiShield } from 'react-icons/fi';
import { adminService, AdminStatsResponse } from '../../services/AdminService';

export function AdminDashboardPage() {
  const toast = useToast();
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await adminService.getStats();
      setStats(data);
    } catch (error: any) {
      toast({
        title: 'Failed to load statistics',
        description: error.response?.data?.detail || 'Please try again',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (!stats) return null;

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Subtitle */}
        <Text color="gray.600">Platform overview and statistics</Text>

        {/* User Stats */}
        <Box>
          <Heading size="sm" mb={4} color="gray.700">
            User Statistics
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Total Users</StatLabel>
                    <StatNumber>{stats.users.total}</StatNumber>
                    <HStack spacing={1} mt={2}>
                      <Icon as={FiUsers} color="gray.500" boxSize={4} />
                      <Text fontSize="sm" color="gray.600">All time</Text>
                    </HStack>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Active Users</StatLabel>
                    <StatNumber color="green.500">{stats.users.active}</StatNumber>
                    <Text fontSize="sm" color="gray.600">
                      {((stats.users.active / stats.users.total) * 100).toFixed(1)}% of total
                    </Text>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Blocked Users</StatLabel>
                    <StatNumber color="red.500">{stats.users.blocked}</StatNumber>
                    <Text fontSize="sm" color="gray.600">
                      {((stats.users.blocked / stats.users.total) * 100).toFixed(1)}% of total
                    </Text>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Admins</StatLabel>
                    <StatNumber color="purple.500">{stats.users.admins}</StatNumber>
                    <HStack spacing={1} mt={2}>
                      <Icon as={FiShield} color="purple.500" boxSize={4} />
                      <Text fontSize="sm" color="gray.600">Platform admins</Text>
                    </HStack>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </Box>

        {/* Registration Trends */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Registered Today</StatLabel>
                  <StatNumber>{stats.users.registered_today}</StatNumber>
                  <StatHelpText>
                    <StatArrow type={stats.users.registered_today > 0 ? 'increase' : 'decrease'} />
                    Last 24 hours
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Registered This Week</StatLabel>
                  <StatNumber>{stats.users.registered_this_week}</StatNumber>
                  <StatHelpText>
                    <Icon as={FiTrendingUp} />
                    Last 7 days
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Registered This Month</StatLabel>
                  <StatNumber>{stats.users.registered_this_month}</StatNumber>
                  <StatHelpText>Last 30 days</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        {/* Invite Stats */}
        <Box>
          <Heading size="sm" mb={4} color="gray.700">
            Invite Statistics
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Total Invites</StatLabel>
                    <StatNumber>{stats.invites.total}</StatNumber>
                    <StatHelpText>Generated all time</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Available</StatLabel>
                    <StatNumber color="blue.500">{stats.invites.available}</StatNumber>
                    <StatHelpText>Ready to use</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Used</StatLabel>
                    <StatNumber color="green.500">{stats.invites.used}</StatNumber>
                    <StatHelpText>Successfully claimed</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat>
                    <StatLabel>Usage Rate</StatLabel>
                    <StatNumber>{(stats.invites.usage_rate * 100).toFixed(1)}%</StatNumber>
                    <StatHelpText>Conversion rate</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </Box>

        {/* Registration Chart */}
        {stats.registrations_chart.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="sm">Registration Trends (Last 30 Days)</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <HStack spacing={2} align="end" h="200px">
                  {stats.registrations_chart.map((item) => (
                    <VStack key={item.date} flex={1} justify="end" spacing={1}>
                      <Box
                        bg="blue.500"
                        width="100%"
                        height={`${(item.count / Math.max(...stats.registrations_chart.map(i => i.count))) * 100}%`}
                        minH="4px"
                        borderRadius="md"
                      />
                      <Text fontSize="xs" color="gray.600" transform="rotate(-45deg)" whiteSpace="nowrap">
                        {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </Text>
                    </VStack>
                  ))}
                </HStack>
              </Box>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
}
